import type {
  IDishDetailsFormValues,
  ICartDishItem,
  ICartDetailsForNormalize,
} from "@/types";

interface INormalizeResult {
  normalized: ICartDishItem;
  changed: boolean;
}

const sortSupplementsStable = (
  supplements: IDishDetailsFormValues["supplements"]
): IDishDetailsFormValues["supplements"] => {
  return [...supplements]
    .map((group) => ({
      supplementId: group.supplementId,
      options: [...(group.options ?? [])]
        .filter((o) => (o.quantity ?? 0) > 0)
        .sort((a, b) => a.optionId - b.optionId),
    }))
    .filter((g) => g.options.length > 0)
    .sort((a, b) => a.supplementId - b.supplementId);
};

export const normalizeCartDishItemAgainstDetails = (
  item: ICartDishItem,
  details: ICartDetailsForNormalize,
  options?: { ensureRequiredSingle?: boolean }
): INormalizeResult => {
  const stableOriginalSupplements = sortSupplementsStable(
    item.supplements ?? []
  );

  const portions = details.portions ?? [];
  const sortedPortions = [...portions].sort(
    (a, b) => Number(a.price ?? 0) - Number(b.price ?? 0)
  );
  const fallbackPortionId = sortedPortions[0]?.id ?? 0;

  const portionExists = portions.some((p) => p.id === item.portionId);
  const nextPortionId = portionExists ? item.portionId : fallbackPortionId;

  const groupById = new Map(details.supplements.map((g) => [g.id, g]));

  let normalizedSupplements = sortSupplementsStable(
    stableOriginalSupplements
      .map((savedGroup) => {
        const actualGroup = groupById.get(savedGroup.supplementId);
        if (!actualGroup) return null;

        const optionIdSet = new Set(
          (actualGroup.options ?? []).map((o) => o.id)
        );

        const filteredOptions = (savedGroup.options ?? []).filter((opt) => {
          const qty = Number(opt.quantity ?? 0);
          if (qty <= 0) return false;
          return optionIdSet.has(opt.optionId);
        });

        if (filteredOptions.length === 0) return null;

        return {
          supplementId: savedGroup.supplementId,
          options: filteredOptions,
        };
      })
      .filter(Boolean) as IDishDetailsFormValues["supplements"]
  );

  if (options?.ensureRequiredSingle) {
    const requiredSingleGroups = (details.supplements ?? []).filter(
      (g) => (g.numberOfOption ?? 0) === 1 && Boolean(g.isRequireOnOrder)
    );

    const chosenGroupIds = new Set(
      normalizedSupplements.map((g) => g.supplementId)
    );

    for (const requiredGroup of requiredSingleGroups) {
      if (chosenGroupIds.has(requiredGroup.id)) continue;

      const firstOptionId = requiredGroup.options?.[0]?.id ?? null;
      if (firstOptionId == null) continue;

      normalizedSupplements = sortSupplementsStable([
        ...normalizedSupplements,
        {
          supplementId: requiredGroup.id,
          options: [{ optionId: firstOptionId, quantity: 1 }],
        },
      ]);
    }
  }

  const normalized: ICartDishItem = {
    ...item,
    portionId: nextPortionId,
    quantity: Math.max(1, Number(item.quantity || 1)),
    supplements: normalizedSupplements,
  };

  const changed =
    normalized.portionId !== item.portionId ||
    normalized.quantity !== item.quantity ||
    JSON.stringify(normalized.supplements) !==
      JSON.stringify(stableOriginalSupplements);

  return { normalized, changed };
};
